/**
 * The languages a therapist's own material can be in.
 *
 * The same seven a public profile lists as spoken, and named by the same
 * locale block — a therapist who says they work in Euskera and a meditation
 * written in Euskera should not be two different words for it.
 *
 * Codes are stored, names are shown: "Castellano" to a Spanish reader and
 * "Spanish" to an English one is the same `es` underneath.
 *
 * The list is a presentation choice, not a rule. The API validates the shape
 * of a code (`es`, `pt-br`) and nothing more, so adding one here is the whole
 * change needed to offer it.
 */
export const CONTENT_LANGUAGES = [
    "es",
    "en",
    "eu",
    "ca",
    "gl",
    "fr",
    "pt",
] as const;

export type ContentLanguage = (typeof CONTENT_LANGUAGES)[number];

/** Where the name of a language lives, for `t()`. */
export const languageLabelKey = (code: string) =>
    `publicDirectory.languages.${code}`;

/**
 * The language to offer as a default, from whatever the interface is in.
 *
 * A therapist writing in the app they read in Spanish is, overwhelmingly,
 * writing in Spanish — but it is a *default*, filled into a visible field they
 * can change, rather than a claim made on their behalf about work already
 * written.
 *
 * `i18n.language` can be a full locale (`es-ES`) or something not offered at
 * all, so it is narrowed to the base code and checked against the list.
 */
export function defaultContentLanguage(
    uiLanguage: string | undefined
): ContentLanguage | "" {
    const base = (uiLanguage ?? "").toLowerCase().split("-")[0];
    return (CONTENT_LANGUAGES as readonly string[]).includes(base)
        ? (base as ContentLanguage)
        : "";
}
